import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';
import { IconChevronDown } from '@tabler/icons-react';
import * as React from 'react';

export type OptionType = Record<'value' | 'label', string>;

export const multiSelectLayouts = {
  default: {
    formItemClasses: 'col-span-3 grid grid-cols-3 items-center gap-4 space-y-0',
    selectTriggerClasses: 'col-span-2 w-full ',
    formMessageClasses: 'col-span-3',
  },
  medium: {
    formItemClasses: '',
    selectTriggerClasses: 'h-14 w-full text-base',
    formMessageClasses: '',
  },
  big: {
    formItemClasses: '',
    selectTriggerClasses: 'h-14 md:h-16 w-full text-base',
    formMessageClasses: '',
  },
};

interface MultiSelectProps {
  options: Record<'value' | 'label', string>[];
  selected: Record<'value' | 'label', string>[];
  onChange: React.Dispatch<
    React.SetStateAction<Record<'value' | 'label', string>[]>
  >;
  className?: string;
  placeholder?: string;
  searchable?: boolean;
  layout?: keyof typeof multiSelectLayouts;
  t?: (key: string) => string;
  onResetCb?: () => void;
  onSaveCb?: () => void;
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      selected,
      onChange,
      className,
      searchable = true,
      layout = 'default',
      onResetCb,
      onSaveCb,
      t,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Backspace' && selected.length > 0) {
          onChange(
            selected.filter((_, index) => index !== selected.length - 1),
          );
        }

        if (e.key === 'Escape') {
          setOpen(false);
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [onChange, selected]);

    const handleOnReset = () => {
      onChange([]);

      onResetCb?.();
    };

    const handleOnSave = () => {
      setOpen(false);

      onSaveCb?.();
    };

    return (
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className={className}>
          <Button
            ref={ref}
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className={cn(
              `w-full justify-between truncate px-3 text-left font-normal`,
              multiSelectLayouts[layout].selectTriggerClasses,
            )}
            onClick={() => setOpen(!open)}
          >
            <div className='w-full truncate font-medium'>
              {selected.map((item) => item.label).join(', ')}
              {selected.length === 0 && (
                <span>
                  {props.placeholder ??
                    (t ? t('forms.labels.select') : 'Select...')}
                </span>
              )}
            </div>
            <IconChevronDown stroke={1.25} className='shrink-0' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) p-0'>
          <Command className={cn('p-0', className)}>
            {searchable && (
              <CommandInput
                placeholder={t ? t('forms.labels.search') : 'Search...'}
              />
            )}
            <CommandList className='px-1 py-1'>
              <CommandEmpty>
                {t ? t('forms.no_items_found') : 'No item found.'}
              </CommandEmpty>

              <CommandGroup
                className='max-h-64 overflow-auto [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-black/40'
                heading={props.placeholder ?? ''}
              >
                {options.map((option) => (
                  <CommandItem key={option.value} asChild>
                    <label
                      htmlFor={option.value}
                      className='flex cursor-pointer gap-2 text-base'
                    >
                      <Checkbox
                        id={option.value}
                        className='data-[state=checked]:bg-brand-green border-input data-[state=checked]:border-brand-green h-5 w-5'
                        checked={selected.some(
                          (item) => item.value === option.value,
                        )}
                        onCheckedChange={() => {
                          const selectedItems = selected.some(
                            (item) => item.value === option.value,
                          )
                            ? selected.filter(
                                (item) => item.value !== option.value,
                              )
                            : [...selected, option];

                          onChange(selectedItems);
                          setOpen(true);
                        }}
                      />

                      {option.label}
                    </label>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            <footer className='border-input mt-2 flex gap-2 border-t px-4 py-4'>
              <Button
                className='flex-1 px-0'
                size='lg'
                variant='outline'
                onClick={handleOnReset}
              >
                {t ? t('forms.labels.reset') : 'Reset'}
              </Button>

              <Button
                className='flex-1 px-0'
                size='lg'
                variant='secondary'
                onClick={handleOnSave}
              >
                {t ? t('forms.labels.save') : 'Save'}
              </Button>
            </footer>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
